import React, { useEffect, useState } from 'react';
import api from '../services/api';

const REFRESH_MS = 60000;

type HealthState = 'online' | 'degraded' | 'offline';

interface StatusSource {
  key: string;
  name: string;
  description: string;
  category: string;
  state: HealthState;
  response_ms: number | null;
  status_code: number | null;
  detail: string;
}

interface StatusResponse {
  checked_at: string;
  overall: HealthState;
  summary: { online: number; degraded: number; offline: number; total: number };
  sources: StatusSource[];
}

const STATE_CONFIG: Record<HealthState, { label: string; color: string; bg: string }> = {
  online:   { label: 'Operational',   color: '#4ade80', bg: 'rgba(74,222,128,0.12)' },
  degraded: { label: 'Degraded',      color: '#fb923c', bg: 'rgba(251,146,60,0.12)' },
  offline:  { label: 'Offline',       color: '#f87171', bg: 'rgba(248,113,113,0.12)' },
};

const CATEGORY_ORDER = ['glsn', 'nws', 'radar', 'map'];
const CATEGORY_LABEL: Record<string, string> = {
  glsn: 'GLSN Services',
  nws: 'National Weather Service',
  radar: 'Radar',
  map: 'Map Tiles',
};

export default function PublicStatusPage() {
  const [data, setData] = useState<StatusResponse | null>(null);
  const [loading, setLoading] = useState(true);
  const [lastRefreshed, setLastRefreshed] = useState<Date | null>(null);

  const load = async () => {
    try {
      const res = await api.get('/status');
      setData(res.data);
      setLastRefreshed(new Date());
    } catch {
      // keep stale data visible if refresh fails
    } finally {
      setLoading(false);
    }
  };

  useEffect(() => {
    load();
    const t = window.setInterval(load, REFRESH_MS);
    return () => window.clearInterval(t);
  }, []);

  const overall = data?.overall ?? 'online';
  const cfg = STATE_CONFIG[overall];

  const grouped = CATEGORY_ORDER.map((cat) => ({
    category: cat,
    sources: (data?.sources ?? []).filter((s) => s.category === cat),
  })).filter((g) => g.sources.length > 0);

  return (
    <div className="page-shell">
      <div className="container" style={{ maxWidth: 680 }}>

        {/* Overall banner */}
        <div style={{
          borderRadius: 16,
          background: cfg.bg,
          border: `1.5px solid ${cfg.color}`,
          padding: '20px 24px',
          marginBottom: 24,
          display: 'flex',
          alignItems: 'center',
          gap: 16,
          flexWrap: 'wrap',
        }}>
          <div style={{
            width: 16, height: 16, borderRadius: '50%',
            background: cfg.color,
            flexShrink: 0,
            boxShadow: `0 0 8px ${cfg.color}`,
          }} />
          <div style={{ flex: 1 }}>
            <p style={{ margin: 0, fontSize: '1.2rem', fontWeight: 800, color: 'var(--ink)' }}>
              {loading ? 'Checking services...' : cfg.label}
            </p>
            {data && (
              <p style={{ margin: '2px 0 0', fontSize: '0.85rem', color: 'var(--muted)' }}>
                {data.summary.online} of {data.summary.total} services operational
                {lastRefreshed && ` · checked ${lastRefreshed.toLocaleTimeString()}`}
              </p>
            )}
          </div>
          <button
            type="button"
            className="btn btn-secondary"
            style={{ fontSize: '0.82rem', padding: '6px 14px' }}
            onClick={load}
          >
            Refresh
          </button>
        </div>

        {/* Per-category groups */}
        {!loading && grouped.map(({ category, sources }) => (
          <div key={category} style={{ marginBottom: 28 }}>
            <p style={{ margin: '0 0 10px', fontSize: '0.78rem', fontWeight: 700, color: 'var(--muted)', textTransform: 'uppercase', letterSpacing: '0.08em' }}>
              {CATEGORY_LABEL[category] ?? category}
            </p>
            <div style={{ display: 'flex', flexDirection: 'column', gap: 10 }}>
              {sources.map((source) => {
                const sc = STATE_CONFIG[source.state];
                return (
                  <div key={source.key} className="card" style={{ padding: '14px 18px' }}>
                    <div style={{ display: 'flex', alignItems: 'center', gap: 12 }}>
                      <div style={{
                        width: 10, height: 10, borderRadius: '50%', flexShrink: 0,
                        background: sc.color,
                        boxShadow: source.state === 'online' ? `0 0 6px ${sc.color}` : 'none',
                      }} />
                      <div style={{ flex: 1, minWidth: 0 }}>
                        <p style={{ margin: 0, fontWeight: 700, fontSize: '0.95rem', color: 'var(--ink)', whiteSpace: 'nowrap', overflow: 'hidden', textOverflow: 'ellipsis' }}>
                          {source.name}
                        </p>
                        <p style={{ margin: '1px 0 0', fontSize: '0.8rem', color: 'var(--muted)' }}>
                          {source.description}
                        </p>
                      </div>
                      <span style={{
                        fontSize: '0.76rem', fontWeight: 700, padding: '3px 10px',
                        borderRadius: 999, background: sc.bg, color: sc.color,
                        border: `1px solid ${sc.color}`, whiteSpace: 'nowrap',
                      }}>
                        {sc.label}
                      </span>
                    </div>
                    {(source.response_ms !== null) && (
                      <p style={{ margin: '8px 0 0 22px', fontSize: '0.78rem', color: 'var(--muted)' }}>
                        {source.response_ms} ms &middot; {source.detail}
                      </p>
                    )}
                  </div>
                );
              })}
            </div>
          </div>
        ))}

        <p style={{ fontSize: '0.78rem', color: 'var(--muted)', textAlign: 'center', marginTop: 8 }}>
          Auto-refreshes every 60 seconds &middot; <a href="/archive" style={{ color: 'var(--muted)' }}>View discussions</a>
        </p>
      </div>
    </div>
  );
}
